/**
 * Microphone capture via getUserMedia + MediaRecorder (push-to-talk MVP).
 * Decodes recorded Blob to 16 kHz mono Float32Array for Whisper.
 */

import { preferredAudioMimeType } from "./BrowserCapability";

const TARGET_SAMPLE_RATE = 16000;

/**
 * @param {Float32Array} input
 * @param {number} inputSampleRate
 * @param {number} targetSampleRate
 * @returns {Float32Array}
 */
export function resampleTo16k(input, inputSampleRate, targetSampleRate = TARGET_SAMPLE_RATE) {
  if (inputSampleRate === targetSampleRate) return input;
  const ratio = inputSampleRate / targetSampleRate;
  const newLength = Math.max(1, Math.round(input.length / ratio));
  const result = new Float32Array(newLength);
  for (let i = 0; i < newLength; i++) {
    const srcIndex = i * ratio;
    const i0 = Math.floor(srcIndex);
    const i1 = Math.min(i0 + 1, input.length - 1);
    const t = srcIndex - i0;
    result[i] = input[i0] * (1 - t) + input[i1] * t;
  }
  return result;
}

/** Reused so each recording does not create/close an AudioContext (browser cap + leak). */
let sharedDecodeCtx = null;

function getSharedDecodeContext() {
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  if (!sharedDecodeCtx || sharedDecodeCtx.state === "closed") {
    sharedDecodeCtx = new AudioCtx();
  }
  return sharedDecodeCtx;
}

/**
 * Decode an audio Blob to mono Float32 PCM at 16 kHz.
 * @param {Blob} blob
 * @returns {Promise<{ pcm: Float32Array, durationSec: number, sampleRate: number }>}
 */
export async function decodeBlobToPcm16k(blob) {
  const arrayBuffer = await blob.arrayBuffer();
  const ctx = getSharedDecodeContext();
  const decoded = await ctx.decodeAudioData(arrayBuffer);
  const channel =
    decoded.numberOfChannels > 1
      ? mixToMono(decoded)
      : decoded.getChannelData(0);
  // Always copy: getChannelData is a view into the AudioBuffer.
  const pcm =
    decoded.sampleRate === TARGET_SAMPLE_RATE
      ? new Float32Array(channel)
      : resampleTo16k(channel, decoded.sampleRate, TARGET_SAMPLE_RATE);
  return {
    pcm,
    durationSec: pcm.length / TARGET_SAMPLE_RATE,
    sampleRate: TARGET_SAMPLE_RATE,
  };
}

function mixToMono(audioBuffer) {
  const len = audioBuffer.length;
  const out = new Float32Array(len);
  const channels = audioBuffer.numberOfChannels;
  for (let c = 0; c < channels; c++) {
    const data = audioBuffer.getChannelData(c);
    for (let i = 0; i < len; i++) out[i] += data[i] / channels;
  }
  return out;
}

export class AudioCapture {
  constructor() {
    this._stream = null;
    this._recorder = null;
    this._chunks = [];
    this._mimeType = null;
    this._audioContext = null;
    this._analyser = null;
    this._rafId = null;
    this._onLevel = null;
    this._startedAt = 0;
  }

  /**
   * @param {{ onLevel?: (level: number) => void }} [opts]
   */
  async start(opts = {}) {
    this._onLevel = opts.onLevel || null;
    this._chunks = [];

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
    } catch (err) {
      const denied =
        err && (err.name === "NotAllowedError" || err.name === "PermissionDeniedError");
      const missing =
        err && (err.name === "NotFoundError" || err.name === "DevicesNotFoundError");
      const e = new Error(
        denied ? "PERMISSION_DENIED" : missing ? "NO_MICROPHONE" : "MIC_ERROR"
      );
      e.name = err?.name || "Error";
      e.cause = err;
      throw e;
    }

    this._stream = stream;
    try {
      this._mimeType = preferredAudioMimeType();
      const recorderOpts = this._mimeType ? { mimeType: this._mimeType } : undefined;
      let recorder;
      try {
        recorder = new MediaRecorder(stream, recorderOpts);
      } catch {
        recorder = new MediaRecorder(stream);
      }
      this._recorder = recorder;
      this._mimeType = recorder.mimeType || this._mimeType || "audio/webm";

      recorder.ondataavailable = (ev) => {
        if (ev.data && ev.data.size > 0) this._chunks.push(ev.data);
      };

      recorder.start(250);
      this._startedAt = performance.now();
      this._startLevelMeter(stream);
    } catch (err) {
      stream.getTracks().forEach((t) => t.stop());
      this._stream = null;
      this._recorder = null;
      throw err;
    }
  }

  _startLevelMeter(stream) {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      this._audioContext = new AudioCtx();
      const source = this._audioContext.createMediaStreamSource(stream);
      this._analyser = this._audioContext.createAnalyser();
      this._analyser.fftSize = 256;
      source.connect(this._analyser);
      const data = new Uint8Array(this._analyser.frequencyBinCount);

      const tick = () => {
        if (!this._analyser) return;
        this._analyser.getByteFrequencyData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i++) sum += data[i];
        const level = Math.min(1, sum / (data.length * 128));
        if (this._onLevel) this._onLevel(level);
        this._rafId = requestAnimationFrame(tick);
      };
      this._rafId = requestAnimationFrame(tick);
    } catch {
      /* level meter optional */
    }
  }

  /**
   * @returns {Promise<{ blob: Blob, durationMs: number, mimeType: string }>}
   */
  async stop() {
    const recorder = this._recorder;
    const stream = this._stream;
    const startedAt = this._startedAt;

    if (this._rafId) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
    if (this._audioContext) {
      await this._audioContext.close().catch(() => {});
      this._audioContext = null;
      this._analyser = null;
    }
    this._onLevel = null;

    const blob = await new Promise((resolve, reject) => {
      if (!recorder || recorder.state === "inactive") {
        resolve(new Blob(this._chunks, { type: this._mimeType || "audio/webm" }));
        return;
      }
      recorder.onstop = () => {
        resolve(new Blob(this._chunks, { type: this._mimeType || "audio/webm" }));
      };
      recorder.onerror = (ev) => reject(ev.error || new Error("RECORDER_ERROR"));
      try {
        recorder.stop();
      } catch (err) {
        reject(err);
      }
    });

    if (recorder) {
      recorder.ondataavailable = null;
      recorder.onstop = null;
      recorder.onerror = null;
    }
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
    }
    this._stream = null;
    this._recorder = null;
    this._chunks = [];

    return {
      blob,
      durationMs: Math.max(0, performance.now() - startedAt),
      mimeType: this._mimeType || blob.type || "audio/webm",
    };
  }

  dispose() {
    if (this._rafId) cancelAnimationFrame(this._rafId);
    this._rafId = null;
    this._onLevel = null;
    if (this._recorder) {
      this._recorder.ondataavailable = null;
      this._recorder.onstop = null;
      this._recorder.onerror = null;
      if (this._recorder.state !== "inactive") {
        try {
          this._recorder.stop();
        } catch {
          /* already stopped */
        }
      }
    }
    if (this._stream) this._stream.getTracks().forEach((t) => t.stop());
    if (this._audioContext) {
      try {
        this._audioContext.close();
      } catch {
        /* ignore */
      }
    }
    this._stream = null;
    this._recorder = null;
    this._audioContext = null;
    this._analyser = null;
    this._chunks = [];
  }
}
